import type { HumanDecisionCard, HumanProvenanceResult } from "../../api/humanProvenance";
import type {
  JournalDecisionOption,
  JournalSensitivityTierOption,
} from "./JournalEntryForm";

function relatedResultForSubject(
  result: HumanProvenanceResult | null,
  subjectArtifactVersionId: string,
): result is HumanProvenanceResult {
  return result?.projection === "related"
    && result.root_ref?.entity_kind === "artifact_version"
    && result.root_ref.id === subjectArtifactVersionId;
}

/** Offer only actual visible DecisionCards; typed refs never become inferred options. */
export function visibleJournalDecisionOptions(
  result: HumanProvenanceResult | null,
  subjectArtifactVersionId: string,
): JournalDecisionOption[] {
  if (!relatedResultForSubject(result, subjectArtifactVersionId)) return [];
  const decisions = result.items.filter(
    (item): item is HumanDecisionCard => item.entity_kind === "decision",
  );
  const seen = new Set<string>();
  return decisions.flatMap((decision) => {
    if (seen.has(decision.ref.id)) return [];
    seen.add(decision.ref.id);
    return [{
      id: decision.ref.id,
      label: `${decision.decision_key} · ${decision.technical_claim_name}`,
      status: decision.current_status,
    }];
  });
}

/** Build an ID-only choice set from facts already disclosed at this exact cut. */
export function visibleJournalSensitivityTiers({
  result,
  subjectArtifactVersionId,
  subjectTier,
}: {
  result: HumanProvenanceResult | null;
  subjectArtifactVersionId: string;
  subjectTier: JournalSensitivityTierOption;
}): JournalSensitivityTierOption[] {
  const tiers = new Map<string, string>([[subjectTier.id, subjectTier.label]]);
  if (relatedResultForSubject(result, subjectArtifactVersionId)) {
    for (const item of result.items) {
      tiers.set(item.sensitivity.tier_id, item.sensitivity.tier_name);
    }
  }
  return [...tiers].map(([id, label]) => ({ id, label }));
}
